import {
  HostedFields,
  HostedFieldsTokenizePayload,
  Client,
  hostedFields,
  client,
} from 'braintree-web';
import {
  HostedFieldsEvent,
  HostedFieldsHostedFieldsCard,
  HostedFieldsState,
  HostedFieldsHostedFieldsFieldData,
  // eslint-disable-next-line import/no-unresolved
} from 'braintree-web/modules/hosted-fields';
import classNames from 'classnames';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { CreditCard, LockMid } from '@zooplus/icons';
import { Alert, Checkbox } from '@zooplus/ui-components';

import ccBackImage from '../../../assets/cc-mini-back.png';
import ccBack2xImage from '../../../assets/cc-mini-back@2x.png';
import ccFrontImage from '../../../assets/cc-mini-front.png';
import ccFront2xImage from '../../../assets/cc-mini-front@2x.png';
import { useConfig } from '../../../config/ConfigProvider';
import { sanitizeCardType, sanitizeCardTypes } from '../../../services/braintreeService';
import {
  BraintreeEventTypes,
  braintreeTracking,
  registrationDataTrafficTracker,
  TrafficNotification,
} from '../../../services/diiService';
import { captureException } from '../../../services/sentryService';
import { CardType, ContainerType, Maybe } from '../../../types';
import { safeStringify } from '../../../utils/utils';
import CardIcon from '../../CardIcon/CardIcon';
import { useErrorContext } from '../../ErrorContextProvider';
import FormControl from '../../FormControl/FormControl';
import FormControlWrapper from '../../FormControl/FormControlWrapper';

import styles from './BraintreeCreditCardForm.module.scss';

type FieldName = 'number' | 'cardholderName' | 'expirationMonth' | 'expirationYear' | 'cvv';

const FIELD_NAMES = ['number', 'cardholderName', 'expirationMonth', 'expirationYear', 'cvv'];

function isFieldName(value: string): value is FieldName {
  return FIELD_NAMES.indexOf(value) !== -1;
}

const DISPLAYABLE_CARD_TYPES: CardType[] = [
  'visa',
  'mastercard',
  'diners-club',
  'american-express',
  'maestro',
];

type TokenizeCardDetails = {
  bin: string;
  cardType: CardType;
  expirationMonth: string;
  expirationYear: string;
  cardholderName: string;
  lastTwo: string;
  lastFour: string;
};

export type FailedTokenizeCardResult = {
  success: false;
  errorMessage: string;
  errorDisplayable: boolean;
};

export type SuccessfulTokenizeCardResult = {
  success: true;
  data: TokenizeCardResultData;
};

export type TokenizeCardResult = FailedTokenizeCardResult | SuccessfulTokenizeCardResult;

export type TokenizeCardResultData = {
  nonce: string;
  details: TokenizeCardDetails;
  type: string;
  description: string;
  binData: any;
  storeInVault: boolean;
};

// Override since typescript types for braintree are not inline with actual implementation ...
export type HostedFieldsOverride = HostedFields & {
  getSupportedCardTypes: () => Promise<string[]>;
};

// Override since typescript types for braintree are not inline with actual implementation ...
type HostedFieldsTokenizePayloadOverride = HostedFieldsTokenizePayload & {
  binData: any;
};

export type BraintreeCreditCardFormHandle = {
  tokenizeCard: () => Promise<TokenizeCardResult>;
};

export type BraintreeCreditCardFormProps = {
  token: string;
  containerType: ContainerType;
  error?: string;
  labelsInline: boolean;
  allowVaulting: boolean;
  onFatalError?: (error: any) => void;
};

type FlagByFieldName = { [key in FieldName]?: boolean };

function maybeBraintreeTracking(containerType: ContainerType, eventType: BraintreeEventTypes) {
  if (containerType === 'registration' || containerType === 'default') {
    braintreeTracking(eventType);
  }
}

function maybeTrafficNotificationError(
  field: string,
  value: HostedFieldsHostedFieldsFieldData,
  isSupported: boolean,
) {
  if (field === 'number' && !isSupported) {
    return 'Unsupported card number';
  } else if (!value.isValid) {
    return 'Invalid value';
  } else if (value.isEmpty) {
    return 'Empty value';
  } else {
    return null;
  }
}

function gatherDataByFieldName(state: HostedFieldsState, isSupported: boolean) {
  const validityByFieldName: FlagByFieldName = {},
    emptinessByFieldName: FlagByFieldName = {},
    trafficNotifications: TrafficNotification[] = [];

  Object.entries(state.fields).forEach(([field, value]) => {
    if (isFieldName(field)) {
      const error = maybeTrafficNotificationError(field, value, isSupported);
      if (error) {
        trafficNotifications.push({ type: 'frontError', field, error });
      }

      validityByFieldName[field] = value.isValid;
      emptinessByFieldName[field] = value.isEmpty;
    }
  });

  return { validityByFieldName, emptinessByFieldName, trafficNotifications };
}

function getDiiEventName(containerType: ContainerType) {
  switch (containerType) {
    case 'registration':
      return 'checkout_registerBT';
    case 'myaccount':
      return undefined;
    default:
      return 'edit_paymentBT';
  }
}

function maybeTrackErrors(
  containerType: ContainerType,
  trafficNotifications: TrafficNotification[],
) {
  const maybeDiiEventName = getDiiEventName(containerType);

  if (maybeDiiEventName) {
    const { setTrackingErrors } = registrationDataTrafficTracker();
    setTrackingErrors(maybeDiiEventName, trafficNotifications);
  }
}

type TokenizeError = {
  code: string;
  details?: any;
  message?: any;
};

function isTokenizeError(value: any): value is TokenizeError {
  return (
    value &&
    value instanceof Object &&
    // eslint-disable-next-line no-prototype-builtins
    value.hasOwnProperty('code') &&
    typeof value.code === 'string'
  );
}

function getInvalidFieldKeys(tokenizeError: TokenizeError) {
  const maybeInvalidFieldKeys = tokenizeError.details?.invalidFieldKeys;
  return maybeInvalidFieldKeys && Array.isArray(maybeInvalidFieldKeys) ? maybeInvalidFieldKeys : [];
}

function getMessage(tokenizeError: TokenizeError) {
  return tokenizeError.message && typeof tokenizeError.message === 'string'
    ? tokenizeError.message
    : '';
}

function makeTrafficNotifications(tokenizeError: unknown) {
  let trafficNotifications: TrafficNotification[];

  if (isTokenizeError(tokenizeError)) {
    switch (tokenizeError.code) {
      case 'HOSTED_FIELDS_FIELDS_EMPTY':
        // All fields are empty
        trafficNotifications = FIELD_NAMES.map((field) => {
          return { type: 'frontError', field, error: 'Empty value' };
        });
        break;
      case 'HOSTED_FIELDS_FIELDS_INVALID':
        // Some fields are invalid
        trafficNotifications = getInvalidFieldKeys(tokenizeError).map((field) => {
          return { type: 'frontError', field, error: 'Invalid value' };
        });
        break;
      case 'HOSTED_FIELDS_TOKENIZATION_FAIL_ON_DUPLICATE':
        // occurs when:
        //   * the client token used for client authorization was generated
        //     with a customer ID and the fail on duplicate payment method
        //     option is set to true
        //   * the card being tokenized has previously been vaulted (with any customer)
        // See: https://developer.paypal.com/braintree/docs/reference/request/client-token/generate#options.fail_on_duplicate_payment_method
        trafficNotifications = [
          {
            type: 'frontError',
            field: '',
            error: "The payment method already exists in the customer's vault",
          },
        ];
        break;
      case 'HOSTED_FIELDS_TOKENIZATION_CVV_VERIFICATION_FAILED':
        // occurs when:
        //   * the client token used for client authorization was generated
        //     with a customer ID and the verify card option is set to true
        //     and you have credit card verification turned on in the Braintree
        //     control panel
        //   * the cvv does not pass verification (https://developer.paypal.com/braintree/docs/reference/general/testing#avs-and-cvv/cid-responses)
        // See: https://developer.paypal.com/braintree/docs/reference/request/client-token/generate#options.verify_card
        trafficNotifications = [
          {
            type: 'frontError',
            field: '',
            error: 'CVV did not pass verification',
          },
        ];
        break;
      case 'HOSTED_FIELDS_FAILED_TOKENIZATION':
        // occurs for any other tokenization error on the server
        trafficNotifications = [
          {
            type: 'frontError',
            field: '',
            error: `Tokenization failed server side with error: ${getMessage(tokenizeError)}`,
          },
        ];
        break;
      case 'HOSTED_FIELDS_TOKENIZATION_NETWORK_ERROR':
        // occurs when the Braintree gateway cannot be contacted
        trafficNotifications = [
          {
            type: 'frontError',
            field: '',
            error: `Network error occurred when tokenizing with error: ${getMessage(
              tokenizeError,
            )}`,
          },
        ];
        break;
      default:
        trafficNotifications = [
          {
            type: 'frontError',
            field: '',
            error: `Something bad happened when tokenizing, error: ${getMessage(tokenizeError)}`,
          },
        ];
    }
  } else {
    trafficNotifications = [
      {
        type: 'frontError',
        field: '',
        error: `Something bad happened when tokenizing, general error: ${safeStringify(
          tokenizeError,
        )}`,
      },
    ];
  }

  return trafficNotifications;
}

const BraintreeCreditCardForm = forwardRef<
  BraintreeCreditCardFormHandle,
  BraintreeCreditCardFormProps
>(({ token, containerType, error, labelsInline, allowVaulting, onFatalError }, forwardedRef) => {
  const braintreeInstance = useRef<Client>();
  const hostedFieldsInstance = useRef<HostedFieldsOverride>();
  const { t } = useTranslation();
  const { setError } = useErrorContext();
  const {
    config: { env },
  } = useConfig();
  const isLocalEnv = env === 'local';

  const [storeInVault, setStoreInVault] = useState(false);
  const [supportedCardTypes, setSupportedCardTypes] = useState<CardType[]>([]);
  const [hostedFieldsCards, setHostedFieldsCards] = useState<HostedFieldsHostedFieldsCard[]>([]);
  const [intialized, setInitialized] = useState(false);

  const [fieldsValidity, setFieldsValidity] = useState<FlagByFieldName>({});
  const [fieldsEmpty, setFieldsEmpty] = useState<FlagByFieldName>({});

  const updateValidity = (newFlagByFieldName: FlagByFieldName) => {
    setFieldsValidity((oldFlagByFieldName) => ({ ...oldFlagByFieldName, ...newFlagByFieldName }));
  };

  const updateEmpty = (newFlagByFieldName: FlagByFieldName) => {
    setFieldsEmpty((oldFlagByFieldName) => ({ ...oldFlagByFieldName, ...newFlagByFieldName }));
  };

  const isInvalid = (name: FieldName) => {
    // undefined means no validation yet so we need to test === false
    return intialized && fieldsValidity[name] === false;
  };

  const isInvalidEmpty = (name: FieldName) => {
    // undefined means no validation yet so we need to test === true
    return intialized && fieldsEmpty[name] === true;
  };

  const getActiveCardConfig = (cardType: CardType) => {
    return hostedFieldsCards.find((card) => sanitizeCardType(card.type) === cardType);
  };

  const isCardTypeActive = (cardType: CardType) => {
    return !!getActiveCardConfig(cardType);
  };

  // Since braintree validity does not take supported card types into account
  // (a card can be of an ussuported type but considered valid anyway)
  // We need a manual validation of card type
  const isCurrentCardNumberSupported = useCallback(() => {
    return (
      !intialized ||
      !!hostedFieldsCards.find((card) => supportedCardTypes.includes(sanitizeCardType(card.type)))
    );
  }, [intialized, hostedFieldsCards, supportedCardTypes]);

  const registerHostedFieldsCallbacks = useCallback(() => {
    hostedFieldsInstance.current?.on('validityChange', (event: HostedFieldsEvent) => {
      if (isLocalEnv) console.log('Card validity changed :', event.fields);
      updateValidity({ [event.emittedBy as FieldName]: event.fields[event.emittedBy]?.isValid });
    });
    hostedFieldsInstance.current?.on('empty', (event: HostedFieldsEvent) => {
      if (isLocalEnv) console.log('Card field empty changed :', event.fields);
      updateEmpty({ [event.emittedBy as FieldName]: event.fields[event.emittedBy]?.isEmpty });
    });
    hostedFieldsInstance.current?.on('notEmpty', (event: HostedFieldsEvent) => {
      if (isLocalEnv) console.log('Card field empty changed :', event.fields);
      updateEmpty({ [event.emittedBy as FieldName]: event.fields[event.emittedBy]?.isEmpty });
    });
    hostedFieldsInstance.current?.on('cardTypeChange', (event: HostedFieldsEvent) => {
      if (isLocalEnv) console.log('Card type changed :', event.cards);
      setHostedFieldsCards(event.cards);
    });
  }, []);

  const handleFatalError = useCallback(
    (error: any) => {
      onFatalError?.(error);
      setError({
        message: t('checkout.payment.creditcard.error.method.unavailable'),
        retryable: false,
      });
    },
    [onFatalError, t, setError],
  );

  const createHostedFields = useCallback(async () => {
    if (isLocalEnv) console.log('Creating new hosted fields instance...');
    try {
      hostedFieldsInstance.current = (await hostedFields.create({
        client: braintreeInstance.current,
        styles: {
          input: {
            'font-size': '15px',
            padding: '15px',
          },
          select: {
            padding: '14px',
            marginRight: '-45px',
            appearance: 'none',
          },
        },
        fields: {
          number: {
            selector: '#cc-number',
            placeholder: t('checkout.creditcard.form.number.placeholder'),
          },
          cardholderName: {
            selector: '#cardholder-name',
            placeholder: t('checkout.creditcard.form.cardholdername.placeholder'),
            maxlength: 15,
          },
          cvv: {
            selector: '#cc-cvv',
            placeholder: t('checkout.creditcard.form.securitycode.placeholder'),
          },
          expirationMonth: {
            selector: '#expiration-month',
            placeholder: t('checkout.creditcard.form.expirationmonth.placeholder'),
            select: {
              options: ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'],
            },
          },
          expirationYear: {
            selector: '#expiration-year',
            placeholder: t('checkout.creditcard.form.expirationyear.placeholder'),
            select: true,
          },
        },
      })) as HostedFieldsOverride;
      if (isLocalEnv) console.log('Braintree hosted fields instance created.');
      setHostedFieldsCards(hostedFieldsInstance.current.getState().cards);
      const types = await hostedFieldsInstance.current.getSupportedCardTypes();
      if (isLocalEnv) console.log('Supported card nice types', types);
      const cardTypes = sanitizeCardTypes(types);
      if (isLocalEnv) console.log('Supported card types', cardTypes);
      setSupportedCardTypes(cardTypes);
      registerHostedFieldsCallbacks();
      setInitialized(true);
    } catch (error: any) {
      captureException({ error, message: 'Braintree hosted fields cannot be created' });
      handleFatalError(error);
    }
  }, [t, registerHostedFieldsCallbacks, handleFatalError]);

  useEffect(() => {
    (async () => {
      try {
        if (isLocalEnv) console.log('Creating new braintree instance...');
        braintreeInstance.current = await client.create({ authorization: token });
      } catch (error: any) {
        // error.code = 'INSTANTIATION_OPTION_REQUIRED' when token is not set
        // error.code = 'CLIENT_INVALID_AUTHORIZATION' when token is not valid
        handleFatalError(error);
        captureException({ error, message: 'Cannot create Braintree client' });
        throw error;
      }
      createHostedFields();
    })();
  }, [createHostedFields, handleFatalError, token]);

  const tokenizeCard = useCallback(async (): Promise<TokenizeCardResult> => {
    setError(undefined);
    const state: HostedFieldsState | undefined = hostedFieldsInstance.current?.getState();
    let result: TokenizeCardResult;
    if (state) {
      // First : check validity from braintree SDK
      const invalidKeys = Object.entries(state.fields)
        .filter((entry) => !entry[1].isValid)
        .map((entry) => entry[0]);
      const isSupported = isCurrentCardNumberSupported();
      // Then : check supported cc validity manually , since braintree does not do it
      if (Object.keys(invalidKeys).length || !isSupported) {
        const { validityByFieldName, emptinessByFieldName, trafficNotifications } =
          gatherDataByFieldName(state, isSupported);

        updateValidity(validityByFieldName);
        updateEmpty(emptinessByFieldName);
        maybeTrackErrors(containerType, trafficNotifications);

        result = {
          success: false,
          errorMessage: `Some fields are invalid : [${invalidKeys.join(', ')}]`,
          errorDisplayable: false,
        };
      } else {
        try {
          const tokenized = (await hostedFieldsInstance.current?.tokenize({
            vault: false,
          })) as Maybe<HostedFieldsTokenizePayloadOverride>;
          if (isLocalEnv) console.log('Tokenization result :', tokenized);
          if (tokenized) {
            maybeBraintreeTracking(containerType, 'BTformOK');
            result = {
              success: true,
              data: {
                ...tokenized,
                details: {
                  ...tokenized.details,
                  cardType: sanitizeCardType(tokenized.details.cardType),
                },
                storeInVault,
              },
            };
          } else {
            captureException({
              message: 'Cannot tokenize hosted fields. Result is empty.',
            });
            result = {
              success: false,
              errorMessage: 'Cannot tokenize hosted fields. Result is empty.',
              errorDisplayable: true,
            };
          }
        } catch (error: unknown) {
          captureException({ error, message: 'Cannot tokenize hosted fields' });
          maybeTrackErrors(containerType, makeTrafficNotifications(error));
          result = {
            success: false,
            errorMessage: 'Cannot tokenize hosted fields',
            errorDisplayable: true,
          };
        }
      }
    } else {
      captureException({ message: 'State is not initialized yet' });
      result = {
        success: false,
        errorMessage: 'State is not initialized yet',
        errorDisplayable: true,
      };
    }
    return result;
  }, [isCurrentCardNumberSupported, setError, storeInVault, containerType]);

  // Hide message when validity changed back to all valid
  useEffect(() => {
    if (Object.values(fieldsValidity).every((v) => v)) {
      setError(undefined);
    }
  }, [fieldsValidity, setError]);

  // For calling from within Ract application
  useImperativeHandle(forwardedRef, () => ({
    tokenizeCard,
  }));

  const handleStoreInVaultChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    setStoreInVault(event.target.checked);
  };

  const isAmex = hostedFieldsCards.length === 1 && isCardTypeActive('american-express');
  const isSupported = isCurrentCardNumberSupported();

  return (
    <div data-zta="braintree-cc-form-container">
      {error && (
        <Alert className={styles.errorMessage} variant="warning">
          {error}
        </Alert>
      )}
      <div className={styles.activeCards}>
        {DISPLAYABLE_CARD_TYPES.filter((type) => supportedCardTypes.includes(type)).map((type) => (
          <CardIcon
            type={type}
            key={type}
            className={classNames(isCardTypeActive(type) && styles.activeCard)}
          />
        ))}
      </div>
      <div className={styles.formWrapper} data-zta="braintree-cc-form">
        <FormControlWrapper
          label={t('checkout.payment.creditcard.card_number')}
          labelFor="cc-number"
          labelInline={labelsInline}
          infoMessage={t('checkout.payment.secure')}
          infoMessageIcon={<LockMid />}
          errorMessage={
            isInvalidEmpty('number')
              ? t('checkout.form.required.error')
              : !isSupported
              ? t('checkout.payment.creditcard.type_not_supported')
              : isInvalid('number') &&
                t('checkout.payment.creditcard.error.form.cardnumber.invalid')
          }
          errorZta="errorInputCreditCardNumber">
          <FormControl
            className={styles.formControl}
            id="cc-number"
            hasError={!isSupported || isInvalid('number') || isInvalidEmpty('number')}
            data-zta="inputCreditCardNumber"
          />
        </FormControlWrapper>
        <FormControlWrapper
          label={t('checkout.payment.creditcard.holder_name')}
          labelFor="cardholder-name"
          labelInline={labelsInline}
          errorMessage={isInvalidEmpty('cardholderName') && t('checkout.form.required.error')}
          errorZta="errorInputCardName">
          <FormControl
            className={styles.formControl}
            hasError={isInvalid('cardholderName') || isInvalidEmpty('cardholderName')}
            id="cardholder-name"
            data-zta="inputCardName"
          />
        </FormControlWrapper>
        <FormControlWrapper
          label={t('checkout.payment.creditcard.expiry_date')}
          labelFor="expiration-month"
          labelInline={labelsInline}
          errorMessage={
            (isInvalidEmpty('expirationMonth') || isInvalidEmpty('expirationYear')) &&
            t('checkout.form.required.error')
          }
          errorZta="errorInputCardDate">
          <div className={styles.expirationDateContainer}>
            <FormControl
              select
              className={styles.expirationDateMonth}
              hasError={isInvalid('expirationMonth') || isInvalidEmpty('expirationMonth')}
              id="expiration-month"
              data-zta="inputCardMonth"
            />
            <FormControl
              select
              className={styles.expirationDateYear}
              hasError={isInvalid('expirationYear') || isInvalidEmpty('expirationYear')}
              id="expiration-year"
              data-zta="inputCardYear"
            />
          </div>
        </FormControlWrapper>
        <FormControlWrapper
          label={
            isAmex
              ? t('checkout.payment.creditcard.cid.generic_code')
              : t('checkout.payment.creditcard.cvc.generic_code')
          }
          labelFor="cc-cvv"
          labelInline={labelsInline}
          infoMessage={
            isAmex
              ? t('checkout.payment.creditcard.cid.warning')
              : t('checkout.payment.creditcard.cvc.warning')
          }
          errorMessage={
            isInvalid('cvv')
              ? t('checkout.form.cvc.min.length.error', { '0': isAmex ? 4 : 3 })
              : isInvalidEmpty('cvv') && t('checkout.form.required.error')
          }
          errorZta="errorInputCardCode">
          <FormControl
            className={styles.cvv}
            id="cc-cvv"
            inputIcon={<CreditCard />}
            hasError={isInvalid('cvv') || isInvalidEmpty('cvv')}
            data-zta="inputCardCode">
            <div>
              {isAmex && (
                <img
                  className={styles.cvvImage}
                  data-zta="cvc-img-amex"
                  alt="cvc-img-amex"
                  src={ccFrontImage}
                  srcSet={`${ccFrontImage} 1x, ${ccFront2xImage} 2x`}
                />
              )}
              {!isAmex && (
                <img
                  className={styles.cvvImage}
                  data-zta="cvc-img-normal"
                  alt="cvc-img-default"
                  src={ccBackImage}
                  srcSet={`${ccBackImage} 1x, ${ccBack2xImage} 2x`}
                />
              )}
            </div>
          </FormControl>
        </FormControlWrapper>
        {allowVaulting && (
          <FormControlWrapper labelInline={labelsInline}>
            <Checkbox
              className={styles.vaultCheck}
              id="vault-check"
              checked={storeInVault}
              onChange={handleStoreInVaultChange}
              data-zta="storeCreditCard">
              {t('checkout.payment.save.creditcard')}
            </Checkbox>
          </FormControlWrapper>
        )}
      </div>
    </div>
  );
});

export default BraintreeCreditCardForm;
